import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { demo } from "service/axiosConfig";
import "react-phone-number-input/style.css";

import { Box, Typography, Card, CardContent, Button, Chip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { callTopics } from "service/axiosConfig";

function DemoPage() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Topics state
  const [topics, setTopics] = useState([]);
  const [formData, setFormData] = useState({ topics: [] });

  // Load topics once
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await callTopics();
        if (res) setTopics(res);
      } catch (err) {
        console.error("Failed to load topics", err);
      }
    };
    fetchTopics();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!name.trim()) newErrors.name = "Please enter your name";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) newErrors.email = "Please enter your email";
    else if (!emailRegex.test(email)) newErrors.email = "Enter a valid email address";
    if (!phone || !isValidPhoneNumber(phone)) newErrors.phone = "Please enter a valid phone number";
    if (formData.topics.length === 0) newErrors.topics = "Please select at least one topic";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const payload = {
        phoneNumber: phone,
        name: name.trim(),
        email: email.trim(),
        topics: formData.topics, // list of topic IDs
      };
      await demo(payload);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(to bottom right, #fff7f0, #ffffff, #f0fff7)",
        fontFamily:
          'var(--default-font-family, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Heartline clickable logo */}
          <Box
            onClick={() => (window.location.href = "https://myheartline.com")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                bgcolor: "#7e2a0c",
                borderRadius: "12px",
              }}
            >
              {/* Client’s Heart SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  width: "20px",
                  height: "20px",
                  display: "block",
                  verticalAlign: "middle",
                }}
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </Box>
            <Typography
              fontWeight="bold"
              sx={{
                fontFamily: "inherit",
                fontSize: "20px",
                color: "#0f172a",
              }}
            >
              Heartline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Title Section */}
      <Box textAlign="center" py={6} px={2}>
        <Typography
          variant="h1"
          fontWeight="bold"
          fontFamily={"inherit"}
          sx={{ fontSize: "36px", mb: 1, color: "#0f172a" }}
        >
          Try Heartline Demo
        </Typography>
        <Typography
          fontFamily={"inherit"}
          sx={{ fontSize: "20px", color: "#475569", maxWidth: 800, mx: "auto" }}
        >
          Experience how Heartline&apos;s daily check-ins and email summaries work. The demo takes
          about 3 minutes for the call and you&apos;ll receive the email summary immediately after.
        </Typography>
      </Box>

      {/* Demo Card */}
      <Card
        variant="outlined"
        sx={{
          maxWidth: 720,
          mx: "auto",
          borderColor: "#ffd29eff !important",
          borderWidth: "2px !important",
          borderRadius: "12px",
        }}
      >
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "64px", // w-16
              height: "64px", // h-16
              mx: "auto",
              mb: 2,
              bgcolor: "#7c2d12", // bg-orange-900
              borderRadius: "16px", // rounded-2xl
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: "32px", height: "32px", color: "#ffffff" }} // w-8 h-8 text-white
            >
              <polygon points="6 3 20 12 6 21 6 3" />
            </svg>
          </Box>

          <Typography fontFamily={"inherit"} fontWeight="400" sx={{ mb: 0.2, fontSize: "24px" }}>
            Interactive Demo
          </Typography>
          <Typography fontFamily={"inherit"} sx={{ fontSize: "16px", color: "#475569", mb: 2 }}>
            Experience daily check-ins and email summaries
          </Typography>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {[
              {
                id: "name",
                label: "Name",
                value: name,
                onChange: setName,
                placeholder: "Enter your name",
              },
              {
                id: "email",
                label: "Email Address",
                value: email,
                onChange: setEmail,
                placeholder: "Enter your email address",
              },
            ].map((field) => (
              <Box key={field.id} textAlign="left" mb={2}>
                <Typography
                  component="label"
                  htmlFor={field.id}
                  sx={{ fontWeight: 500, fontSize: "14px", color: "#0f172a" }}
                >
                  {field.label}
                </Typography>
                {/* Input */}
                <Box
                  component="input"
                  id={field.id}
                  type={field.id === "email" ? "email" : "text"}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={field.placeholder}
                  required
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    padding: "10px 12px",
                    backgroundColor: "#f8fafc",
                    border: "0px solid #e2e8f0",
                    borderRadius: "calc(var(--radius, 6px) + 4px)",
                    outline: "none",
                    width: "100%",
                    color: "#0f172a",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:focus": {
                      borderColor: "#7c2d12",
                      boxShadow: "0 0 0 1px #7c2d12",
                    },
                  }}
                />
                {errors[field.id] && <Typography sx={errorStyle}>{errors[field.id]}</Typography>}
              </Box>
            ))}

            {/* Phone */}
            <Box
              textAlign="left"
              mb={2}
              sx={{
                "& .PhoneInputInput": {
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  background: "#f8fafc",
                  fontSize: "0.9rem",
                  fontFamily: "inherit",
                  padding: "10px 12px",
                  borderRadius: "12px",
                  width: "100%",
                  boxSizing: "border-box",
                  color: "#0f172a",
                  height: "16px",
                },
                "& .PhoneInput--focus": {
                  borderColor: "#7c2d12",
                  boxShadow: "0 0 0 1px #7c2d12",
                },
              }}
            >
              <Typography
                component="label"
                htmlFor="phone"
                sx={{ fontWeight: 500, fontSize: "14px", color: "#0f172a" }}
              >
                Mobile Phone #
              </Typography>
              <PhoneInput
                id="phone"
                value={phone}
                onChange={setPhone}
                defaultCountry="US"
                countries={["US"]}
                disableArrowIcon
                disableCountryPicker={true}
                international={false}
                addInternationalOption={false}
                placeholder="Enter your mobile phone number"
                style={{
                  width: "100%",
                  fontSize: "0.9rem",
                  fontFamily: "inherit",
                  padding: "10px 12px",
                  backgroundColor: "#f8fafc",
                  border: "0px solid #e2e8f0",
                  borderRadius: "12px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {errors.phone && <Typography sx={errorStyle}>{errors.phone}</Typography>}
            </Box>
            {/* Topics */}
            <Box
              mb={2}
              textAlign="left"
              sx={{
                fontFamily: "inherit",
              }}
            >
              <Typography fontWeight={500} fontSize={14} mb={1}>
                Select Topics (max 3)
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)" },
                  gap: 1.5,
                }}
              >
                {topics.map((item) => {
                  const selected = formData.topics.includes(item.id);

                  return (
                    <Chip
                      key={item.id}
                      label={item.name}
                      clickable
                      onClick={() => {
                        setFormData((prev) => {
                          if (selected) {
                            // remove
                            return {
                              ...prev,
                              topics: prev.topics.filter((id) => id !== item.id),
                            };
                          }
                          if (prev.topics.length >= 3) {
                            toast.info("You can select maximum 3 topics");
                            return prev;
                          }
                          return { ...prev, topics: [...prev.topics, item.id] };
                        });
                      }}
                      sx={{
                        backgroundColor: selected ? "rgb(86,25,25)" : "#f0f0f0",
                        color: selected ? "#fff" : "#000",
                        fontSize: 12,
                        fontFamily: "inherit",
                        borderRadius: "6px",
                        height: 28,
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </Box>
              {errors.topics && <Typography color="error">{errors.topics}</Typography>}
            </Box>
            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "calc(var(--radius, 6px) + 4px)",
                fontSize: "0.85rem",
                color: "#7c2d12",
                textAlign: "left",
              }}
            >
              {/* Title Row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: "20px", height: "20px", color: "#9a3412" }}
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <Typography
                  component="h4"
                  fontFamily={"inherit"}
                  fontWeight={500}
                  sx={{ fontSize: "1rem", color: "#7c2d12" }}
                >
                  Demo Experience
                </Typography>
              </Box>

              {/* Description */}
              <Typography
                fontFamily={"inherit"}
                sx={{ fontSize: "0.85rem", color: "oklch(.47 .157 37.304)" }}
              >
                This demo includes daily check-ins and email summaries. The call takes about 3
                minutes, and you&apos;ll receive the email immediately after.
              </Typography>
            </Box>

            {/* Submit */}
            <Button
              type="submit"
              fullWidth
              disabled={isSubmitting}
              sx={{
                mt: 2,
                bgcolor: "#7c2d12",
                "&:hover": { bgcolor: "#65200d", color: "#ffffff" },
                py: 0.6, // tighter
                fontWeight: 600,
                fontFamily: "inherit",
                textTransform: "none",
                color: "#ffffff",
                fontSize: "14px",
                borderRadius: "calc(var(--radius, 6px) + 4px)",
                fontFamily: "inherit",
                boxShadow: "none",
              }}
            >
              Start Demo Experience
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Experience Section */}
      <Box textAlign="center" py={6} px={2}>
        <Typography
          variant="h2"
          fontFamily="inherit"
          fontWeight="bold"
          sx={{ fontSize: "30px", mb: 4, color: "#0f172a" }}
        >
          What You&apos;ll Experience in the Demo
        </Typography>
        <Box sx={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
          {[
            {
              icon: (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "48px", // w-12
                    height: "48px", // h-12
                    bgcolor: "#ffedd5", // bg-orange-100
                    borderRadius: "12px", // rounded-xl
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: "24px", height: "24px", color: "#9a3412" }} // text-orange-800
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </Box>
              ),
              title: "Daily Check-in Call",
              text: "Experience a real wellness call conversation. Our AI conducts caring, natural check-ins that feel like talking to a caring friend.",
            },
            {
              icon: (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "48px", // w-12
                    height: "48px", // h-12
                    bgcolor: "#dcfce7", // bg-green-100
                    borderRadius: "12px", // rounded-xl
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: "24px", height: "24px", color: "#16a34a" }} // text-green-600
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                </Box>
              ),
              title: "Email Summary",
              text: "Receive a detailed email summary immediately after the call with key insights about wellness and any important notes.",
            },
          ].map((item, i) => (
            <Card
              key={i}
              sx={{
                flex: "1 1 250px",
                maxWidth: 440,
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>{item.icon}</Typography>
              <Typography
                variant="h4"
                fontFamily="inherit"
                fontWeight={400}
                sx={{ mb: 1, fontSize: "18px", color: "#0f172a" }}
              >
                {item.title}
              </Typography>
              <Typography fontFamily="inherit" sx={{ fontSize: "14px", color: "#475569" }}>
                {item.text}
              </Typography>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Comparison Section */}
      <Box textAlign="center" py={6} px={2} maxWidth="1120px" mx="auto">
        <Typography
          variant="h3"
          fontFamily={"inherit"}
          fontWeight="500"
          sx={{ fontSize: "24px", mb: 4, color: "#0f172a" }}
        >
          Demo vs Full Heartline Service
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            justifyContent: "center",
            fontFamily: "inherit",
          }}
        >
          <Card
            sx={{
              flex: "1 1 300px",
              border: "1px solid #fdba74",
              borderRadius: "calc(var(--radius, 6px) + 4px)",
              p: 3,
              textAlign: "left",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={500}
              sx={{ mb: 1, color: "#7c2d12", fontSize: "18px" }}
            >
              Demo Experience
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: "1.2rem",
                m: 0,
                color: "#475569", // slategray
                fontSize: "16px",
                listStyleType: "disc",
                "& li": {
                  marginBottom: "0.25rem",
                  "&::marker": {
                    color: "#dc2626", // red-600
                    fontSize: "1.2em",
                  },
                },
              }}
            >
              <li>Daily check-in call (sample)</li>
              <li>Email summary report</li>
            </Box>
          </Card>

          <Card
            sx={{
              flex: "1 1 300px",
              border: "1px solid #86efac",
              borderRadius: "calc(var(--radius, 6px) + 4px)",
              p: 3,
              textAlign: "left",
            }}
          >
            <Typography fontWeight={500} sx={{ mb: 1, color: "#166534", fontSize: "18px" }}>
              Full Paid/Trial Service
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: "1.2rem",
                m: 0,
                color: "#475569", // slategray
                fontSize: "16px",
                listStyleType: "disc",
                "& li": {
                  marginBottom: "0.25rem",
                  "&::marker": {
                    color: "#16a34a", // green-600
                    fontSize: "1.2em",
                  },
                },
              }}
            >
              <li>Daily check-in calls</li>
              <li>Email summary reports</li>
              <li>Health dashboard with trends</li>
              <li>Smart alerts for changes</li>
              <li>Healthcare provider sharing</li>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Testimonial Section */}
      <Box maxWidth="800px" mx="auto" mb={6} px={2}>
        <Card
          sx={{
            borderRadius: "calc(var(--radius, 6px) + 4px)",
            p: 4,
            textAlign: "center",
            alignItems: "center",
            bgcolor: "white",
          }}
        >
          <Box
            sx={{
              width: "64px", // w-16
              height: "64px", // h-16
              bgcolor: "#e2e8f0", // bg-slate-200
              borderRadius: "50%", // rounded-full
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <span style={{ fontSize: "1.5rem", lineHeight: 1 }}>👩</span>
          </Box>

          <Typography
            fontFamily={"inherit"}
            sx={{ fontSize: "20px", color: "#475569", mb: 2, fontStyle: "italic" }}
          >
            &quot;The demo showed me exactly how Heartline would work for my mom. Seeing the actual
            reports and hearing a sample check-in call gave me confidence that this was right for
            our family.&quot;
          </Typography>
          <Typography
            fontFamily={"inherit"}
            fontWeight={600}
            sx={{ fontSize: "16px", color: "#475569", fontStyle: "italic" }}
          >
            — Sarah M., Daughter and Caregiver
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}

/* Shared styles */
const inputStyle = {
  width: "100%",
  fontSize: "0.9rem",
  fontFamily: "inherit",
  padding: "10px 12px",
  backgroundColor: "#f8fafc",
  border: "0px solid #e2e8f0",
  borderRadius: "12px",
  outline: "none",
  boxSizing: "border-box",
};

const errorStyle = {
  color: "red",
  fontSize: "0.75rem",
  marginTop: "0.25rem",
};

export default DemoPage;
